import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const CONTENT = {
    de: {
        badge: "Produktpartner",
        title_1: "Wir arbeiten mit",
        title_2: "Keune.",
        sub: "Niederländische Premium-Haarpflege für Farbe, Pflege und Styling, die halten.",
        buy_label: "Zum Mitnehmen",
        buy_text: "Ausgewählte Keune-Produkte können Sie direkt bei uns im Salon kaufen.",
    },
    bs: {
        badge: "Partner",
        title_1: "Radimo sa brendom",
        title_2: "Keune.",
        sub: "Holandska premium njega kose za boju, njegu i styling koji traju.",
        buy_label: "Za ponijeti kući",
        buy_text: "Odabrane Keune proizvode možete kupiti direktno kod nas u salonu.",
    },
    en: {
        badge: "Product partner",
        title_1: "We work with",
        title_2: "Keune.",
        sub: "Dutch premium hair care for colour, care and styling that lasts.",
        buy_label: "Take home",
        buy_text: "Selected Keune products are available for purchase directly at the salon.",
    },
    it: {
        badge: "Partner",
        title_1: "Lavoriamo con",
        title_2: "Keune.",
        sub: "Hair care premium olandese per colore, cura e styling che durano.",
        buy_label: "Da portare a casa",
        buy_text: "Prodotti Keune selezionati sono disponibili all'acquisto direttamente in salone.",
    },
    fr: {
        badge: "Partenaire",
        title_1: "Nous travaillons avec",
        title_2: "Keune.",
        sub: "Soin capillaire premium néerlandais pour une couleur, un soin et un styling qui durent.",
        buy_label: "À emporter",
        buy_text: "Une sélection de produits Keune est disponible à l'achat directement au salon.",
    },
    pl: {
        badge: "Partner",
        title_1: "Pracujemy z marką",
        title_2: "Keune.",
        sub: "Holenderska premium pielęgnacja włosów — kolor, pielęgnacja i stylizacja, które trwają.",
        buy_label: "Na wynos",
        buy_text: "Wybrane produkty Keune są dostępne do kupienia bezpośrednio w salonie.",
    },
};

export default function KeunePartner() {
    const { lang } = useLanguage();
    const c = CONTENT[lang] || CONTENT.de;
    return (
        <section className="relative bg-white py-14 md:py-28 border-b border-gold/15" data-testid="keune-partner">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                {/* Header row */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                    <div className="lg:col-span-4">
                        <div className="flex items-center gap-3">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-gold/60">
                                <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                            </span>
                            <span className="eyebrow">{c.badge}</span>
                        </div>
                    </div>
                    <div className="lg:col-span-8">
                        <motion.h3
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className="font-display text-ink text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight"
                        >
                            {c.title_1}{" "}
                            <span className="serif-italic text-gold">{c.title_2}</span>
                        </motion.h3>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-5 text-ink/70 text-base md:text-lg leading-relaxed max-w-2xl"
                        >
                            {c.sub}
                        </motion.p>
                    </div>
                </div>

                {/* Keune feature card */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-12 lg:mt-16 pb-14 border-b border-gold/15"
                >
                    <div
                        className="group relative flex items-center justify-center h-36 md:h-48 rounded-3xl border bg-cream border-gold/40 overflow-hidden"
                        data-testid="brand-keune"
                    >
                        <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full border border-gold/20" />
                        <div className="absolute -bottom-16 -left-10 w-72 h-72 rounded-full border border-gold/15" />
                        <span className="relative font-display italic text-5xl md:text-7xl leading-none text-gold tracking-tight">
                            Keune
                        </span>
                        <span className="absolute top-5 left-6 text-[0.6rem] tracking-[0.35em] uppercase text-gold-deep">
                            Amsterdam · Since 1922
                        </span>
                    </div>
                </motion.div>

                {/* Retail note */}
                <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-4">
                        <div className="flex items-center gap-3">
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gold/60 text-gold-deep">
                                <ShoppingBag className="w-4 h-4" />
                            </span>
                            <span className="eyebrow">{c.buy_label}</span>
                        </div>
                    </div>
                    <p className="lg:col-span-8 text-ink/75 text-base md:text-lg leading-relaxed max-w-2xl">
                        {c.buy_text}
                    </p>
                </div>
            </div>
        </section>
    );
}
